import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLeagues } from '../../../hooks/useQueries';
import { identityFor } from '../../../data/leagues';
import { NationalFlagEmblem } from '../NationalFlagEmblems';
import { getOverlayRoot, lockAppChrome } from '../../../lib/overlayRoot';
import { triggerHaptic } from '../../../lib/haptics';
import { EASE_OUT_IOS, SPRING_POP, useReducedMotion } from '../../../lib/motion';

/**
 * A deliberate "entering a new world" transition for league switches. Tapping a
 * league fires this: a full-screen branded overlay takes over, the new league's
 * crest stamps in, a loading bar sweeps, then it lifts to reveal the destination —
 * which has already navigated underneath. It's intentionally theatrical (the page
 * cross-fade alone was too subtle to feel) and buys the new screen time to mount.
 *
 * The overlay lives at the persistent app frame (not inside a screen) so it survives
 * the navigation it triggers. `playLeagueSwitch(leagueId, run)` shows the overlay,
 * runs `run` (the actual navigate) once it's covered the screen, then auto-dismisses.
 */
type PlayFn = (leagueId: string, run: () => void) => void;

// Default (no provider) just navigates — the theater is purely additive.
const LeagueSwitchCtx = createContext<PlayFn>((_, run) => run());

export function useLeagueSwitch(): PlayFn {
  return useContext(LeagueSwitchCtx);
}

type SwitchState = { id: number; leagueId: string } | null;

export function LeagueSwitchProvider({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  const [state, setState] = useState<SwitchState>(null);
  const timers = useRef<number[]>([]);
  const seq = useRef(0);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  const play = useCallback<PlayFn>(
    (leagueId, run) => {
      clearTimers();
      const id = ++seq.current;
      setState({ id, leagueId });
      void triggerHaptic('medium', { force: true });

      // Navigate once the overlay has covered the screen; settle the "loading" with a
      // success tick; then lift the overlay to reveal the (already-mounted) screen.
      const navDelay = reduce ? 110 : 240;
      const holdEnd = reduce ? 360 : 880;
      timers.current.push(window.setTimeout(() => run(), navDelay));
      if (!reduce) {
        timers.current.push(
          window.setTimeout(() => void triggerHaptic('success', { force: true }), holdEnd - 140)
        );
      }
      timers.current.push(window.setTimeout(() => setState(null), holdEnd));
    },
    [reduce]
  );

  useEffect(() => () => clearTimers(), []);

  return (
    <LeagueSwitchCtx.Provider value={play}>
      {children}
      <LeagueSwitchOverlay state={state} reduce={reduce} />
    </LeagueSwitchCtx.Provider>
  );
}

function LeagueSwitchOverlay({ state, reduce }: { state: SwitchState; reduce: boolean }) {
  const { data: leagues } = useLeagues();

  // Hold the app chrome locked (tab bar hidden) while the overlay owns the screen.
  useEffect(() => {
    if (!state) return;
    const unlock = lockAppChrome();
    return unlock;
  }, [state]);

  if (typeof document === 'undefined') return null;

  const league = state ? leagues?.find((l) => l.leagueId === state.leagueId) : undefined;
  const identity = state ? identityFor(state.leagueId) : undefined;
  const name = league?.name ?? identity?.name ?? 'League';
  const country = league?.country ?? identity?.country;
  const slug = league?.cycling?.slug;

  return createPortal(
    <AnimatePresence>
      {state && (
        <motion.div
          key={state.id}
          className="pointer-events-auto fixed inset-0 flex flex-col items-center justify-center gap-6 px-10"
          style={{
            // Solid base + a vertically *centered* radial glow: with the centre at
            // 50% 50% the top and bottom edges sit at the same radius, so both iOS
            // safe areas (notch + home indicator) read the exact same colour and the
            // overlay fills the screen seamlessly edge to edge.
            backgroundColor: '#08080C',
            backgroundImage:
              'radial-gradient(circle at 50% 50%, #181820 0%, #0E0E14 52%, #08080C 100%)'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.16 : 0.2, ease: EASE_OUT_IOS }}
        >
          {/* Crest — stamps in with a spring, a soft accent glow pulsing behind it. */}
          <motion.div
            className="relative grid place-items-center"
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.55, rotate: -12 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, rotate: 0 }}
            transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_POP}
          >
            {!reduce && (
              <motion.span
                aria-hidden
                className="pointer-events-none absolute h-[150px] w-[150px] rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(36,197,106,0.5), rgba(36,197,106,0) 68%)'
                }}
                initial={{ opacity: 0.8, scale: 0.6 }}
                animate={{ opacity: [0.8, 0.35, 0.55], scale: [0.6, 1.25, 1.05] }}
                transition={{ duration: 0.9, ease: EASE_OUT_IOS }}
              />
            )}
            <div className="relative drop-shadow-[0_14px_30px_rgba(0,0,0,0.55)]">
              <div style={{ transform: 'scale(1.85)' }}>
                <NationalFlagEmblem leagueId={state.leagueId} country={country} slug={slug} size="lg" />
              </div>
            </div>
          </motion.div>

          {/* Name + eyebrow. */}
          <div className="flex flex-col items-center text-center">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/45">
              Entering
            </span>
            <motion.span
              className="mt-2 max-w-[80vw] truncate text-[26px] font-extrabold tracking-[-0.02em] text-white"
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={reduce ? { duration: 0.18 } : { ...SPRING_POP, delay: 0.06 }}
            >
              {name}
            </motion.span>
          </div>

          {/* The "loading" sweep — a bar that fills as the new world loads in. */}
          <div className="mt-1 h-[5px] w-[168px] overflow-hidden rounded-full bg-white/[0.12]">
            <motion.div
              className="h-full w-full rounded-full"
              style={{
                transformOrigin: 'left',
                background: 'linear-gradient(90deg,#2EDB7B,#0BA078)'
              }}
              initial={{ scaleX: reduce ? 1 : 0.04 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: reduce ? 0.18 : 0.66, ease: EASE_OUT_IOS }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}
